import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, IconButton } from "@mui/material";
import { useState } from "react";
import ErrorHelper from "@/components/ErrorWrapper";
import SignatureInput from "@/components/signiture/SignatureInput";
import { useSignatureController } from "@/components/signiture/use-signature-controller";
import { CCM_CONSENT_STYLE, CCM_CONSENT_TEXT } from "../ccm-consent-constant";
import { useConsentReadOnly } from "../hooks/use-consent-form-sync";

export interface ConsentSignaturePadProps {
  label: string;
  value?: string;
  onChange: (signature?: string) => void;
  required?: boolean;
  error?: string;
}

const ConsentSignaturePad = ({
  label,
  value,
  onChange,
  required,
  error,
}: ConsentSignaturePadProps) => {
  const isReadOnly = useConsentReadOnly();
  const [padVersion, setPadVersion] = useState(false);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    padVersion,
    value
  );

  const resetPad = () => {
    signPad?.clear();
    setPadVersion((previous) => !previous);
  };

  const captureSignature = () => {
    if (!isSignEmpty()) onChange(signPad?.toDataURL());
  };

  if (value || isReadOnly) {
    return (
      <ErrorHelper label={label} required={required} error={error}>
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={CCM_CONSENT_STYLE.SIGNATURE_WIDTH}
          height={CCM_CONSENT_STYLE.SIGNATURE_HEIGHT}
          sx={{
            border: 1,
            borderColor: CCM_CONSENT_STYLE.BORDER,
            borderRadius: 1,
          }}
        >
          {value && (
            <img
              src={value}
              alt={label}
              style={{ maxHeight: "100%", maxWidth: "100%" }}
            />
          )}
          {!isReadOnly && (
            <IconButton
              color="error"
              size="small"
              aria-label={CCM_CONSENT_TEXT.SIGNATURE_REMOVE}
              onClick={() => {
                resetPad();
                onChange(undefined);
              }}
              sx={{ position: "absolute", top: -14, right: -14 }}
            >
              <HighlightOffIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </ErrorHelper>
    );
  }

  return (
    <Box
      position="relative"
      width={CCM_CONSENT_STYLE.SIGNATURE_WIDTH}
      onPointerUp={captureSignature}
      onMouseLeave={captureSignature}
    >
      <SignatureInput
        label={label}
        canvasRef={canvasRef}
        w={CCM_CONSENT_STYLE.SIGNATURE_WIDTH}
        h={CCM_CONSENT_STYLE.SIGNATURE_HEIGHT}
        required={required}
        error={error}
      />
      <Button
        size="small"
        variant="contained"
        onClick={resetPad}
        sx={{ position: "absolute", bottom: 8, right: 8, height: 24 }}
      >
        {CCM_CONSENT_TEXT.SIGNATURE_CLEAR}
      </Button>
    </Box>
  );
};

export default ConsentSignaturePad;
